import { Injectable, TemplateRef } from '@angular/core';
import { CryptoService } from './crypto-service';
import { HcrService } from './hcr-service.service';
import { ClinicDataService } from './data-service/clinic-data-service';
import { UserCache } from './user-cache';
import { DateHelper } from '../shared/helpers/date-helper';
import { generateUUID } from '../shared/utilities';
import { BaseConfig } from './base-config.service';
import { VPNState } from './start-up-service';
import { DengueFormDto, DengueUploadDto, ToccApi, ToccParameter, ToccResult } from './api-service/hist/tocc-api';
import { HandleErrorService } from './handle-error.service';
import { EasyNotificationService } from './easy-notification.service';
import { MainLayoutService } from '../layout/services/main-layout.service';
import { WholeHist } from './api-service/hist/whole-hist';
import { Announcement } from './api-service/announcement/announcement-model';
import { AlertOn } from './announceAlert.service';
import { AnnouncementApi } from './api-service/announcement/announcement-api';
import { RegisterConst } from '../shared/constants/register-const';
import { UserConfirmService } from './user-confirm.service';
import { HelperSourceEnum } from '../hist/enums/helper-source-enum';
import { HistHelperPadService, SetItemEvent } from '../hist/pad/hist-helper-pad.service';
import { HistService } from '../hist/services/hist.service';

interface verifyMac {
  name: string;
  code: string;
  mac: string;
}

interface queryParameter {
  regInstitutionCode: string;
  personalIdNumber: string;
  newbornFlag: boolean;
  birthDate?: string;
}

interface queryMain {
  mac: verifyMac;
  data: queryParameter;
}

interface pagingInfo {
  pages: number;
  previous: number;
  next: number;
  page: number;
  rowsPerPage: number;
  totalItems: number;
}

interface queryResult {
  uid: string;
  name: string;
  personalIdNumber: string;
  newbornBirthDate: string;
  birthDate?: string;
  regInstitutionCode: string;
  regInstitutionName: string;
  homeCity: string;
  homeDistrict: string;
  homeVillage: string;
  homeAddress: string;
  workCity: string;
  workDistrict: string;
  workVillage: string;
  workAddress: string;
  symptoms: string;
  countries: string;
  mosquito: string;
  sprayArea: string;
  othersFever: string;
  createTime: string;
}

interface queryResponse {
  code: number;
  message: string;
  result: queryResult;
  paging: pagingInfo;
}

interface uploadParameter {
  name?: string;
  personalIdNumber?: string;
  birthDate?: string;
  newbornFlag?: boolean;
  address?: string;
  icdMain?: string;
  icdSub?: string;
  regInstitutionCode: string;
  regInstitutionName: string;
  opdDate?: string;
  medicalRecordTime: string;
  hisKey: string;
  dengueUid?: string;
  toccFlag: boolean;
}

interface uploadMain {
  mac: verifyMac;
  data: uploadParameter;
}

interface uploadResponse {
  code: number;
  message: string;
  paging: pagingInfo;
}

export interface bulletinParameter {
  regInstitutionCode: string;
}

interface bulletinMain {
  mac: verifyMac;
  data: bulletinParameter;
}

export interface bulletinResult {
  content: string;
  type: string;
  institutionAreaList: string[];
  institutionList: string[];
  institutionExcludeList: string[];
  patientAreaList: string[];
  updateTime: string;
}

export interface bulletinResponse {
  code: number;
  message: string;
  result: bulletinResult[];
  paging: pagingInfo;
}

export class TOCCQueryResult{
  dengueFormDto:DengueFormDto = null;
  dengueFlag :'Y' | 'N' | '' = '';
  dengueResult:string = '';
  keepToccMessage:string = ''
  dengueRecordTime:Date
}
enum siteType {
  Internet = 'Internet',
  VPN = 'VPN',
  Test = 'Test'
}

enum endPointType {
  Query = 'Query',
  Upload = 'Upload',
  Bulletin = 'Bulletin'
}

export const ToccKeyword = 'TOCC';
export const ToccNotFilled = 'TOCC：未填寫';
const ToccBlank = 'TOCC：空白訊息。';
const ToccPrefix = 'CC:';
const VendorName = 'ASIAVISION'; //翰鼎指定的廠商名稱
const VendorCode = '7054410400'; //自訂編號(建議10字元以上)

@Injectable({
  providedIn: 'root'
})
export class ToccService {

  constructor(
    private cryptoService: CryptoService,
    private hcrService: HcrService,
    private clinicDataService: ClinicDataService,
    private handleErrorService: HandleErrorService,
    private notification: EasyNotificationService,
    private userConfirmService:UserConfirmService,
    private mainLayoutService: MainLayoutService,
    private toccApi: ToccApi,
    private announcementApi:AnnouncementApi,
    private helperPadService:HistHelperPadService,
    private histService:HistService
  ) {
    histService.onHistLoaded2.subscribe(x=>{
      
    })
   }

  private InternetSite = 'https://tocc-api.kchb.gov.tw';//後續讀參數設定
  private VPNSite = 'https://tocc-api-vpn.kchb.gov.tw';//後續讀參數設定
  private TestSite = 'https://tocc-api-preprod.hding.com.tw';//後續讀參數設定
  private QueryEndPoint = '/v1/his/dengue-form';
  private UploadEndPoint = '/v1/his/patient-visit-record';
  private BulletinEndPoint = '/v1/his/bulletin';
  private UseConsoleCallApi = true; //是否使用主控台呼叫API(Test/Internet/VPN), 否則使用Server呼叫API(Test/Internet)
  private vpnState: VPNState;

  get isDemoMode(): boolean {
    return this.hcrService.isDemoMode;
  }

  public async isEnabledTOCC(vpnState: VPNState): Promise<boolean> {
    this.vpnState = vpnState;
    var cln = UserCache.getLoginUser().Clinic;
    var enable = false;
    this.InternetSite = '';
    this.VPNSite = '';
    this.TestSite = '';
    this.UseConsoleCallApi = true;
    const paramHst001 = await this.clinicDataService.getParam("HST001");
    if (paramHst001) {
      if (paramHst001.TOCCInternetSite) this.InternetSite = paramHst001.TOCCInternetSite;
      if (paramHst001.TOCCVPNSite) this.VPNSite = paramHst001.TOCCVPNSite;
      if (paramHst001.TOCCTestSite) this.TestSite = paramHst001.TOCCTestSite;
      this.UseConsoleCallApi = paramHst001.TOCCApiMode == 1;

      if (paramHst001.TOCCEnable && paramHst001.TOCCClinicArea) {
        var toccAreas = paramHst001.TOCCClinicArea.split(',').filter(x => x);
        var clinicAddr = (cln.City??'') + (cln.Area??'') + (cln.Street??'');
        if (toccAreas.some(x => clinicAddr.indexOf(x) >= 0)) {
          if (paramHst001.TOCCEndDate) {
            if (DateHelper.today <= DateHelper.getDate(paramHst001.TOCCEndDate)) {
              enable = paramHst001.TOCCEnable;
            }
          } else {
            enable = paramHst001.TOCCEnable;
          }
        }
      }
    }
    return enable;
  }

  private async getDengueForm(parameter: queryParameter) {
    var name = VendorName;
    var code = VendorCode;
    var data = parameter.regInstitutionCode;
    // var result: queryResponse;
    // await this.cryptoService.generateHMAC(code, data).then(async (mac) => {
    //   var verify: verifyMac = { name: name, code: code, mac: mac };
    //   var content: queryMain = { mac: verify, data: parameter };
    //   result = await this.callDengueForm(content);
    // });
    var mac = await this.cryptoService.generateHMAC(code, data);
    var verify: verifyMac = { name: name, code: code, mac: mac };
    var content: queryMain = { mac: verify, data: parameter };
    var result = await this.callToccApi(content, endPointType.Query);
    return result;
  }

  private async uploadPatientVisitRecord(parameter: uploadParameter) {
    var name = VendorName;
    var code = VendorCode;
    var data = parameter.regInstitutionCode;
    var mac = await this.cryptoService.generateHMAC(code, data);
    var verify: verifyMac = { name: name, code: code, mac: mac };
    var content: uploadMain = { mac: verify, data: parameter };
    var result = await this.callToccApi(content, endPointType.Upload);
    return result;
  }

  public async getBulletin(parameter: bulletinParameter) {
    var name = VendorName;
    var code = VendorCode;
    var data = parameter.regInstitutionCode;
    var mac = await this.cryptoService.generateHMAC(code, data);
    var verify: verifyMac = { name: name, code: code, mac: mac };
    var content: bulletinMain = { mac: verify, data: parameter };
    var result = await this.callToccApi(content, endPointType.Bulletin);
    return result;
  }

  private async callToccApi(content: any, apiType: endPointType) {
    var useConsole = this.UseConsoleCallApi && this.hcrService.ConnectStatus;
    var [url, apiUrlType] = this.getUrl(useConsole, 'Auto', apiType);
    // [url, apiUrlType] = this.getUrl(siteType.Internet, apiType);//整合測試當時用正式站台，正式上線需註解
    var jsonContent = JSON.stringify(content);
    var ret: any;
    if (useConsole) {
      ret = await this.hcrService.sendMsg({ Call: 'SendFromUrl', Args: { Url: url, Content: jsonContent } });
    } else {
      var parameter: ToccParameter = { Url: url, JsonContent: jsonContent };
      ret = await this.toccApi.CallTocc(parameter);
    }
    if (ret.Successed) {
      return [ret.Returns, jsonContent, apiUrlType];
    } else {
      throw [ret.Returns, jsonContent, apiUrlType];
    }
  }

  private getUrl(useConsole: boolean, envType: siteType | 'Auto', apiType: endPointType) {
    var site = '';
    if (this.isDemoMode) {
      site = this.TestSite;
    } else {
      switch (envType) {
        case siteType.Internet:
          site = this.InternetSite;
          break;
        case siteType.VPN:
          site = this.VPNSite;
          break;
        case siteType.Test:
          site = this.TestSite;
          break;
        case 'Auto':
          // site = environment.production ? this.VPNSite : this.TestSite;
          // site = BaseConfig.isDebug() ? this.TestSite : this.VPNSite;
          if (useConsole) {
            site = BaseConfig.isDebug() ? this.TestSite : (this.vpnState === 'Fail' ? this.InternetSite : this.VPNSite);
          } else {
            site = BaseConfig.isDebug() ? this.TestSite : this.InternetSite;
          }
          break;
        default:
          site = this.TestSite;
          break;
      }
    }

    var apiUrlType = '';
    if (site === this.InternetSite) {
      apiUrlType = siteType.Internet;
    } else if (site === this.VPNSite) {
      apiUrlType = siteType.VPN;
    } else if (site === this.TestSite) {
      apiUrlType = siteType.Test;
    }

    var endPoint = '';
    switch (apiType) {
      case endPointType.Query:
        endPoint = this.QueryEndPoint;
        break;
      case endPointType.Upload:
        endPoint = this.UploadEndPoint;
        break;
      case endPointType.Bulletin:
        endPoint = this.BulletinEndPoint;
        break;
      default:
        endPoint = '';
        break;
    }
    return [site + endPoint, apiUrlType];
  }

  public async getHMAC(data: string) {
    var result = '';
    await this.cryptoService.generateHMAC(VendorCode, data).then((hmac) => {
      result = hmac;
    });
    return result;
  }

  public async queryDengue(isEnabledTOCC: boolean, histRcd: WholeHist, needRead: boolean):
    Promise<[string, string, DengueFormDto, ('Y' | 'N' | ''), string]> {
    var dengueResult = '';
    var dengueFlag: ('Y' | 'N' | '') = '';
    var dengueFormDto = this.newDengueFormDto(
      histRcd.Patient.PatientNo,
      histRcd.Patient.Id,
      histRcd.Patient.CName,
      histRcd.Patient.CId ? histRcd.Patient.CId : histRcd.Patient.MomCId
    );
    var toccMessage = '';
    var originalTOCC = '';

    if (isEnabledTOCC && needRead) {
      this.mainLayoutService.showLoader();
      try {
        var cln = UserCache.getLoginUser().Clinic;
        var parameter: queryParameter = {
          regInstitutionCode: cln.NHICode,
          personalIdNumber: dengueFormDto.PersonalIdNumber,
          newbornFlag: (!histRcd.Patient.CId && !!histRcd.Patient.MomCId),
          birthDate: DateHelper.getFormatedDateString(histRcd.Patient.Birthday, false, '-', false)
        };

        dengueFormDto.QueryTime = new Date();

        var [retResult, retParameter, retApiUrlType] = await this.getDengueForm(parameter);

        dengueFormDto.ApiUrlType = retApiUrlType;
        dengueFormDto.QueryParameter = retParameter;
        dengueFormDto.QueryResponse = retResult;

        var dengueForm = (retResult ? JSON.parse(retResult) as queryResponse : null);
        if (dengueForm != null) {
          toccMessage = dengueForm?.message.replace(ToccPrefix, '').replace(/\n/g, ' ');
          if (!toccMessage) toccMessage = ToccBlank;

          dengueFormDto.QueryReturnCode = dengueForm?.code;
          dengueFormDto.QueryReturnMessage = toccMessage;

          if (dengueForm.code != 200) {
            this.notification.showError('TOCC查詢問卷失敗: ' + '(' + dengueForm.code.toString() + ')' + toccMessage);
          } else {
            var historyId = histRcd.Hist.Id;
            if (historyId) {
              try {
                var lastQueryReturnMessage = await this.toccApi.GetLastQueryReturnMessage(historyId);
                if (lastQueryReturnMessage) {
                  dengueFormDto.HistoryId = lastQueryReturnMessage.HistoryId;
                  dengueFormDto.HISKey = lastQueryReturnMessage.HISKey;
                  originalTOCC = lastQueryReturnMessage.QueryReturnMessage.trim();
                }
              }
              catch (ex) {
                // this.notification.showError('讀取TOCC問卷紀錄發生錯誤: ' + ex);
              }
            }

            dengueResult = dengueForm.result ? JSON.stringify(dengueForm.result) : '';
            dengueFlag = dengueForm.result ? 'Y' : 'N';

            var form: queryResult = (dengueResult ? JSON.parse(dengueResult) as queryResult : null);
            dengueFormDto.UId = form?.uid;
            dengueFormDto.Name = form?.name ?? histRcd.Patient.CName;
            dengueFormDto.PersonalIdNumber = form?.personalIdNumber ?? parameter.personalIdNumber;
            dengueFormDto.NewbornBirthDate = form?.newbornBirthDate ? DateHelper.getDate(form.newbornBirthDate) : null;
            dengueFormDto.FormCreateTime = form?.createTime ? new Date(form.createTime) : null;
            dengueFormDto.TOCCFlag = dengueFlag === 'Y';
          }
        } else {
          dengueFormDto.QueryReturnCode = -1;
          dengueFormDto.QueryReturnMessage = 'TOCC查詢問卷失敗';

          this.notification.showError(dengueFormDto.QueryReturnMessage);
        }
      } catch (ex) {
        var [retResult, retParameter, retApiUrlType] = Array.isArray(ex) ? ex : [ex, null, null];

        dengueFormDto.ApiUrlType = retApiUrlType;
        dengueFormDto.QueryParameter = retParameter;
        dengueFormDto.QueryResponse = retResult;

        dengueFormDto.QueryReturnCode = -99;
        dengueFormDto.QueryReturnMessage = 'TOCC查詢問卷發生錯誤: ' + retResult;

        this.notification.showError(dengueFormDto.QueryReturnMessage);
      } finally {
        await this.writeDengueForm('Create', dengueFormDto);
        this.mainLayoutService.hideLoader();
      }
    }

    return [toccMessage, originalTOCC, dengueFormDto, dengueFlag, dengueResult];
  }

  public async uploadDengue(dxChanged: boolean, manual: boolean,
    dengueUploadDto: DengueUploadDto, dengueFormDto: DengueFormDto, dengueFlag: ('Y' | 'N' | ''), dengueResult: string):
    Promise<[boolean, Date, DengueFormDto, ('Y' | 'N' | '')]> {
    var result = false;

    var haveLast = false;
    //既有病歷修改且有改主次診斷，則讀取TOCC問卷紀錄，且紀錄是有填TOCC問卷，則需再次上傳患者就醫紀錄。
    //或是手動再次上傳
    if (!dengueFlag) {
      if (dxChanged || manual) {
        try {
          var lastDengueForm = await this.toccApi.GetLastDengueForm(dengueUploadDto.HistoryId);
          if (lastDengueForm) {
            if (lastDengueForm.TOCCFlag || manual) {
              dengueFormDto = lastDengueForm;
              dengueFormDto.Id = null;//清空Id後續跑新增紀錄，就不是修改紀錄
              dengueFlag = lastDengueForm.TOCCFlag ? 'Y' : 'N';
              haveLast = true;
            }
          } else {
            dengueFormDto = this.newDengueFormDto(dengueUploadDto.PatientNo, dengueUploadDto.PatientId, dengueUploadDto.Name, dengueUploadDto.PersonalIdNumber);
            dengueFlag = 'N';
          }
        }
        catch (ex) {
          this.notification.showError('讀取TOCC問卷紀錄發生錯誤: ' + ex);
        }
      }
    }

    if (dengueFlag) {
      var cln = UserCache.getLoginUser().Clinic;
      var fill = (dengueFlag === 'Y');
      //另一種產生UUID的方式
      // let uuid = uuidv4(); //import {v4 as uuidv4} from 'uuid';
      var uuid = dengueFormDto.HISKey ? dengueFormDto.HISKey : generateUUID();
      var parameter: uploadParameter = {
        regInstitutionCode: cln.NHICode,
        regInstitutionName: cln.Name,
        // birthDate: DateHelper.getFormatedDateString(dengueUploadDto.Birthday, false, '-', false),
        opdDate: DateHelper.getFormatedDateString(dengueUploadDto.RegDate, false, '-', false),
        medicalRecordTime: DateHelper.getFormatedDateString(dengueUploadDto.EditTime, false, '-', false) + ' ' + DateHelper.getTimeString(dengueUploadDto.EditTime, ':', true),
        hisKey: uuid,
        toccFlag: fill
      };
      //讀取TOCC問卷時->回應有填寫TOCC(toccFlag=true)->上傳患者就醫紀錄的參數(才需要填以下欄位)
      if (fill) {
        var form: queryResult = (dengueResult ? JSON.parse(dengueResult) as queryResult : null);
        var personalIdNumber = haveLast ? dengueFormDto.PersonalIdNumber : form?.personalIdNumber;
        var dengueUid = haveLast ? dengueFormDto.UId : form?.uid;
        var sub = [];
        if (dengueUploadDto.Dx2) sub.push(dengueUploadDto.Dx2 + dengueUploadDto.Dx2Name);
        if (dengueUploadDto.Dx3) sub.push(dengueUploadDto.Dx3 + dengueUploadDto.Dx3Name);
        if (dengueUploadDto.Dx4) sub.push(dengueUploadDto.Dx4 + dengueUploadDto.Dx4Name);
        if (dengueUploadDto.Dx5) sub.push(dengueUploadDto.Dx5 + dengueUploadDto.Dx5Name);
        if (dengueUploadDto.Dx6) sub.push(dengueUploadDto.Dx6 + dengueUploadDto.Dx6Name);
        parameter.name = dengueUploadDto.Name;
        parameter.personalIdNumber = personalIdNumber;
        parameter.birthDate = DateHelper.getFormatedDateString(dengueUploadDto.Birthday, false, '-', false);
        parameter.newbornFlag = (!dengueUploadDto.PersonalIdNumber && !!dengueUploadDto.MomCId);
        parameter.address = (dengueUploadDto.City??'') +(dengueUploadDto.Area??'') + (dengueUploadDto.Street??'');
        parameter.icdMain = dengueUploadDto.Dx1 + dengueUploadDto.Dx1Name;
        parameter.icdSub = sub.join(',');
        parameter.dengueUid = dengueUid;
      }

      dengueFormDto.HistoryId = dengueUploadDto.HistoryId;
      dengueFormDto.RecordTime = new Date();
      dengueFormDto.HISKey = uuid;
      dengueFormDto.TOCCFlag = fill;

      try {
        var [retResult, retParameter, retApiUrlType] = await this.uploadPatientVisitRecord(parameter);

        dengueFormDto.ApiUrlType = retApiUrlType;
        dengueFormDto.RecordParameter = retParameter;
        dengueFormDto.RecordResponse = retResult;

        var visitRecord = (retResult ? JSON.parse(retResult) as uploadResponse : null);
        if (visitRecord != null) {
          dengueFormDto.RecordReturnCode = visitRecord?.code;
          dengueFormDto.RecordReturnMessage = visitRecord?.message;

          var toccMessage = visitRecord.message ? visitRecord.message : '';
          if (visitRecord.code != 200) {
            this.notification.showError('TOCC上傳患者就醫紀錄失敗: ' + '(' + visitRecord.code.toString() + ')' + toccMessage);
          } else {
            result = true;

            this.notification.showInfo('TOCC上傳患者就醫紀錄成功');
          }
        } else {
          dengueFormDto.RecordReturnCode = -1;
          dengueFormDto.RecordReturnMessage = 'TOCC上傳患者就醫紀錄失敗';

          this.notification.showError(dengueFormDto.RecordReturnMessage);
        }
      } catch (ex) {
        var [retResult, retParameter, retApiUrlType] = Array.isArray(ex) ? ex : [ex, null, null];

        dengueFormDto.ApiUrlType = retApiUrlType;
        dengueFormDto.RecordParameter = retParameter;
        dengueFormDto.RecordResponse = retResult;

        dengueFormDto.RecordReturnCode = -99;
        dengueFormDto.RecordReturnMessage = 'TOCC上傳患者就醫紀錄發生錯誤: ' + retResult;

        this.notification.showError(dengueFormDto.RecordReturnMessage);
      }

      var ret = await this.writeDengueForm('Update', dengueFormDto);
    }

    return [result && ret, dengueFormDto.RecordTime, dengueFormDto, dengueFlag];
  }

  private newDengueFormDto(patientNo: string, patientId: number, name: string, personalIdNumber: string): DengueFormDto {
    var cln = UserCache.getLoginUser().Clinic;
    var dengueFormDto = new DengueFormDto();
    dengueFormDto.ClinicId = cln.Id;
    dengueFormDto.ClinicCode = cln.Code;
    dengueFormDto.PatientNo = patientNo;
    dengueFormDto.PatientId = patientId;
    dengueFormDto.RegInstitutionCode = cln.NHICode;
    dengueFormDto.RegInstitutionName = cln.Name;

    dengueFormDto.Name = name;
    dengueFormDto.PersonalIdNumber = personalIdNumber;
    return dengueFormDto;
  }

  private async writeDengueForm(type: 'Create' | 'Update', dengueFormDto: DengueFormDto): Promise<boolean> {
    try {
      if (type === 'Create') {
        var ret = await this.toccApi.CreateDengueForm(dengueFormDto);
        dengueFormDto.Id = ret.Id;
      } else if (type === 'Update') {
        if (dengueFormDto.Id) {
          await this.toccApi.UpdateDengueForm(dengueFormDto);
        } else {
          await this.toccApi.CreateDengueForm(dengueFormDto);
        }
      }

      return true;
    }
    catch (ex) {
      let errorMsg = this.handleErrorService.handleError(ex);
      this.notification.showError('TOCC問卷紀錄: ' + errorMsg);
    }

    return false;
  }

//#region 啟動
  public async Initial(vpnState: VPNState){
    var isEnabledTOCC = await this.isEnabledTOCC(vpnState);
    if (isEnabledTOCC) {
      var cln = UserCache.getLoginUser().Clinic;
      var parameter: bulletinParameter = {
        regInstitutionCode: cln.NHICode
      };
      var dengueFormDto = new DengueFormDto();
      dengueFormDto.ClinicId = cln.Id;
      dengueFormDto.ClinicCode = cln.Code;
      dengueFormDto.PatientNo = '';
      dengueFormDto.PatientId = 0;
      dengueFormDto.QueryTime = new Date();
      dengueFormDto.RegInstitutionCode = cln.NHICode;
      dengueFormDto.RegInstitutionName = cln.Name;

      try {
        var [retResult, retParameter, retApiUrlType] = await this.getBulletin(parameter);

        dengueFormDto.ApiUrlType = retApiUrlType;
        dengueFormDto.QueryParameter = retParameter;
        dengueFormDto.QueryResponse = retResult;

        var bulletin = (retResult ? JSON.parse(retResult) as bulletinResponse : null);
        if (bulletin != null) {
          var toccMessage = bulletin.message ? bulletin.message : '';
  
          dengueFormDto.QueryReturnCode = bulletin?.code;
          dengueFormDto.QueryReturnMessage = toccMessage;
  
          if (bulletin.code != 200) {
            this.notification.showError('TOCC讀取公告失敗: ' + '(' + bulletin.code.toString() + ')' + toccMessage);
          } else {
            var anyPublishInstitution: boolean[] = [];
            var anyPublishPatient: boolean[] = [];
            await Promise.all(bulletin.result.map(async (b) => {
              var [isPublishInstitution, isPublishPatient] = await this.createTOCCAnnouncement(b);
                anyPublishInstitution.push(isPublishInstitution);
                anyPublishPatient.push(isPublishPatient);
              })).then(async () => {
                if ((anyPublishInstitution.length == 0 && anyPublishPatient.length == 0) ||
                  (anyPublishInstitution.every(x => x === false) && anyPublishPatient.every(x => x === false))) {
                  await this.expireTOCCAnnouncement('05,06');
                } else {
                  if (anyPublishInstitution.every(x => x === false)) await this.expireTOCCAnnouncement('05');
                  if (anyPublishPatient.every(x => x === false)) await this.expireTOCCAnnouncement('06');
                }
              });
            }
          } else {
            dengueFormDto.QueryReturnCode = -1;
            dengueFormDto.QueryReturnMessage = 'TOCC讀取公告失敗';
  
            this.notification.showError(dengueFormDto.QueryReturnMessage);
          }
        } catch (ex) {
          var [retResult, retParameter, retApiUrlType] = Array.isArray(ex) ? ex : [ex, null, null];
  
          dengueFormDto.ApiUrlType = retApiUrlType;
          dengueFormDto.QueryParameter = retParameter;
          dengueFormDto.QueryResponse = retResult;
  
          dengueFormDto.QueryReturnCode = -99;
          dengueFormDto.QueryReturnMessage = 'TOCC讀取公告發生錯誤: ' + retResult;
  
          this.notification.showError(dengueFormDto.QueryReturnMessage);
        } finally {
          try {
            await this.toccApi.CreateDengueForm(dengueFormDto);
          } catch (ex) {
            // this.notify.showError('TOCC建立紀錄發生錯誤: ' + ex);
          }
        }
      }
    
  }

  async createTOCCAnnouncement(result: bulletinResult) {
    if (!result) return [null, null];

    var cln = UserCache.getLoginUser().Clinic;
    var clinicCode = cln.NHICode;
    var clinicAddr = (cln.City ?? '') + (cln.Area ?? '') + (cln.Street ?? '');
    var isPublishInstitution = false;
    var isPublishPatient = false;
    // 0:全部發佈, 註：原TOCC政令，依照上述條件顯示於登入頁或診間首頁
    // 1:指定醫事機構行政區, 註：同上
    // 2:指定醫事機構, 註：同上
    // 3:指定病患通訊地址行政區域, 註：原TOCC警示，顯示於病歷製作頁；病人地址或機構地址符合條件就要顯示
    if (result.type == '0') {
      isPublishInstitution = true;
    } else if (result.type == '1' && result.institutionAreaList && result.institutionAreaList.length > 0) {
      var toccAreas = result.institutionAreaList.filter(x => x);
      if (toccAreas.some(x => clinicAddr.indexOf(x) >= 0)) {
        isPublishInstitution = true;
      }
    } else if (result.type == '2' && result.institutionList && result.institutionList.length > 0 && clinicCode) {
      if (result.institutionList.some(x => x == clinicCode)) {
        isPublishInstitution = true;
      }
    } else if (result.type == '3' && result.patientAreaList && result.patientAreaList.length > 0) {
      //先存起來，等看診時才判斷病患地址
      isPublishPatient = true;
    }

    if (result.institutionExcludeList && result.institutionExcludeList.length > 0 &&
      result.institutionExcludeList.some(x => x == clinicCode)) {
      isPublishInstitution = false;
      isPublishPatient = false;
    }

    if (isPublishInstitution || isPublishPatient) {
      var announcement = new Announcement();
      announcement.CompanyId = Number(cln.CompanyId);
      announcement.TypeCode = 'IMP';
      announcement.ClassCode = isPublishInstitution ? '05' : '06';//TOCC政令/TOCC警示
      announcement.Title = '高雄市衛生局TOCC' + (isPublishInstitution ? '政令' : '警示');
      announcement.Content = result?.content ?? '';
      if (announcement.Content) {
        var addition = '';
        if (isPublishPatient) {
          addition += '病患通訊地址行政區域: ' + result.patientAreaList.join(',') + '\n';
        }
        if (result.updateTime) {
          addition += '更新時間: ';
          if (!isNaN(Date.parse(result.updateTime))) {
            addition += DateHelper.formatROCDateTime(result.updateTime, false, false) + ' ' + DateHelper.getTimeString(new Date(result.updateTime), ':', true) + '\n';
          } else {
            addition += result.updateTime + '\n';
          }
        }
        addition += '高雄市政府衛生局\n';

        announcement.Content += '\n\n' + addition;
      }
      announcement.StartTime = DateHelper.today;
      announcement.EndTime = DateHelper.addMonthDay(DateHelper.today, 6);
      var alertOn: AlertOn[] = [];
      if (isPublishInstitution) {
        alertOn.push({ AlertOn: 'LOGIN', AlertCondition: '', AlertConditionCol: '', AlertConditionOp: '', AlertConditionValue: '' });
      } else {
        alertOn.push({ AlertOn: 'OPD', AlertCondition: 'patient', AlertConditionCol: 'street', AlertConditionOp: 'some', AlertConditionValue: result.patientAreaList.join(',') });
      }
      announcement.AlertOn = JSON.stringify(alertOn);
      try {
        //dataScope: 0全部, 1院所公告, 2系統公告(全院所)
        await this.announcementApi.CreateSpecific(announcement, [], 1);
      }
      catch (ex) {
        this.notification.showError('建立TOCC公告發生錯誤: ' + ex);
        return [null, null];
      }
    }

    return [isPublishInstitution, isPublishPatient];
  }

  async expireTOCCAnnouncement(classCodes: string) {
    var cln = UserCache.getLoginUser().Clinic;
    var announcement = new Announcement();
    announcement.CompanyId = Number(cln.CompanyId);
    announcement.ClassCode = classCodes;
    try {
      await this.announcementApi.ExpireSpecific(announcement, [], 1);
    }
    catch (ex) {
      this.notification.showError('過期TOCC公告發生錯誤: ' + ex);
    }
  }
//#endregion


async onTOCCQueryClick(histRcd:WholeHist,currentCC:string) {
  if (await this.isEnabledTOCC(this.vpnState)) {
    var ret = await this.userConfirmService.showConfirm({
      title: 'TOCC重新查詢',
      msg: '是否確定重新查詢問卷填寫內容?\n\n查詢結果將顯示於主訴中，並於完診時，自動上傳患者就醫紀錄。',
      textYes: '確定',
      textNo: '取消',
      fitContent: true
    });

    if (ret) {
      await this.queryDengueToCC(true,histRcd,currentCC);
    }
  }
}
queryResult: TOCCQueryResult;
async queryDengueToCC(manual: boolean,histRcd:WholeHist,currentCC:string) {
  var toccMessage = '';
  var originalTOCC = '';
  this.queryResult = new TOCCQueryResult()
  var isEnable = await this.isEnabledTOCC(this.vpnState);
  [toccMessage, originalTOCC, this.queryResult.dengueFormDto, this.queryResult.dengueFlag, this.queryResult.dengueResult] = await this.queryDengue(
    isEnable, histRcd,
    (histRcd.Register.StatusCode != RegisterConst.Code_Status_Finish) || manual
  );

  if (toccMessage) {
    var modifyCC= histRcd.Hist.CC;
    if (manual) modifyCC = currentCC;

    if (modifyCC && modifyCC?.indexOf(ToccKeyword) >= 0) {
      //原本如有TOCC先清除-來自TOCC問卷紀錄
      if (originalTOCC){
        modifyCC = modifyCC.replace(originalTOCC, '');
      } 
      //原本如有TOCC先清除-來自還沒寫入TOCC問卷紀錄前的TOCC
      if (this.queryResult.keepToccMessage) {
        modifyCC = modifyCC.replace(this.queryResult.keepToccMessage, '');
      }
      //如有未填寫一律清除
      modifyCC = modifyCC.replace(ToccNotFilled, '');
    }
    modifyCC = toccMessage + '\n' + (modifyCC ?? '');
    this.helperPadService.SetItem(new SetItemEvent(HelperSourceEnum.CC, { Name: modifyCC }, false, null));
    this.queryResult.keepToccMessage = toccMessage;
  }
}

async onTOCCUploadClick(histRcd:WholeHist,originHistRcd:WholeHist,toccUploadMessage:TemplateRef<any>) {
  
  if (this.isEnabledTOCC(this.vpnState) && histRcd.Register.StatusCode == RegisterConst.Code_Status_Finish) {
    if (!this.queryResult.dengueRecordTime) await this.getLastRecordTime(histRcd);
    var showRecordTime = '上次上傳時間: ' +
      (this.queryResult.dengueRecordTime ?
        DateHelper.getROCFullDateString(new Date(this.queryResult.dengueRecordTime), '/') + ' ' + DateHelper.getTimeString(this.queryResult.dengueRecordTime, ':', true) :
        '無');
    var showHint = this.queryResult.dengueRecordTime ? '' : '(必須執行上傳)';
    var showColor = this.queryResult.dengueRecordTime ? '' : 'red';
    var ret = await this.userConfirmService.showConfirm({
      title: 'TOCC重新上傳',
      msg: '是否確定重新上傳患者就醫紀錄?',
      textYes: '確定',
      textNo: '取消',
      fitContent: true,
      template: toccUploadMessage,
      templateData: { Color: showColor, Message: showRecordTime, Hint: showHint }
    });

    if (ret) {
      var result: boolean;
      [result, this.queryResult.dengueRecordTime, this.queryResult.dengueFormDto, this.queryResult.dengueFlag] = await this.uploadDengue(
        this.histService.DxChanged(originHistRcd, histRcd),
        true,
        this.newDengueUploadDto(histRcd),
        this.queryResult.dengueFormDto,
        this.queryResult.dengueFlag,
        this.queryResult.dengueResult);
    }
  }
}
async getLastRecordTime(histRcd:WholeHist) {
  if (histRcd.Register.StatusCode == RegisterConst.Code_Status_Finish && histRcd.Hist.Id) {
    try {
      var lastRecordTime = await this.toccApi.GetLastRecordTime(histRcd.Hist.Id);
      if (lastRecordTime && lastRecordTime.RecordTime) {
        this.queryResult.dengueRecordTime = lastRecordTime.RecordTime;
      }
    }
    catch (ex) {
      // this.notification.showError('讀取TOCC最新上傳時間發生錯誤: ' + ex);
    }
  }
}
newDengueUploadDto(histRcd:WholeHist) {
  var dengueUploadDto = new DengueUploadDto();
  dengueUploadDto.HistoryId = histRcd.Hist.Id;
  dengueUploadDto.PatientNo = histRcd.Patient.PatientNo;
  dengueUploadDto.PatientId = histRcd.Patient.Id;
  dengueUploadDto.Name = histRcd.Patient.CName;
  dengueUploadDto.PersonalIdNumber = histRcd.Patient.CId;
  dengueUploadDto.RegDate = histRcd.Register.RegDate;
  dengueUploadDto.EditTime = histRcd.Hist.EditTime;
  dengueUploadDto.Dx1 = histRcd.Hist.Dx1;
  dengueUploadDto.Dx2 = histRcd.Hist.Dx2;
  dengueUploadDto.Dx3 = histRcd.Hist.Dx3;
  dengueUploadDto.Dx4 = histRcd.Hist.Dx4;
  dengueUploadDto.Dx5 = histRcd.Hist.Dx5;
  dengueUploadDto.Dx6 = histRcd.Hist.Dx6;
  dengueUploadDto.Dx1Name = histRcd.Hist.Dx1Name;
  dengueUploadDto.Dx2Name = histRcd.Hist.Dx2Name;
  dengueUploadDto.Dx3Name = histRcd.Hist.Dx3Name;
  dengueUploadDto.Dx4Name = histRcd.Hist.Dx4Name;
  dengueUploadDto.Dx5Name = histRcd.Hist.Dx5Name;
  dengueUploadDto.Dx6Name = histRcd.Hist.Dx6Name;
  dengueUploadDto.Birthday = histRcd.Patient.Birthday;
  dengueUploadDto.MomCId = histRcd.Patient.MomCId;
  dengueUploadDto.City = histRcd.Patient.City;
  dengueUploadDto.Area = histRcd.Patient.Area;
  dengueUploadDto.Street = histRcd.Patient.Street;
  return dengueUploadDto;
}

public async 登革熱TOCC上傳IfEnabled(histRcd:WholeHist,originHistRcd:WholeHist){
  if (this.isEnabledTOCC(this.vpnState)) {
    await this.uploadDengue(
      this.histService.DxChanged(originHistRcd, histRcd),
      false,
      this.newDengueUploadDto(histRcd),
      this.queryResult.dengueFormDto,
      this.queryResult.dengueFlag,
      this.queryResult.dengueResult
    );
  }
}
  public async removeTOCCFromCC(historyId:number, cc:string):Promise<string>{
    var copyCC = cc;
    if (this.isEnabledTOCC) {
      
      var originalTOCC = '';
      if (historyId) {
        try {
          var lastQueryReturnMessage = await this.toccApi.GetLastQueryReturnMessage(historyId);
          if (lastQueryReturnMessage) {
            originalTOCC = lastQueryReturnMessage.QueryReturnMessage.trim();
          }
        }
        catch (ex) {
          // this.notification.showError('讀取TOCC問卷紀錄發生錯誤: ' + ex);
        }
        
        if (copyCC && copyCC.indexOf(ToccKeyword) >= 0) {
          //原本如有TOCC先清除-來自TOCC問卷紀錄
          if (originalTOCC) copyCC = copyCC.replace(originalTOCC, '');
          //如有未填寫一律清除
          copyCC = copyCC.replace(ToccNotFilled, '');
        }
      }
    }
    return copyCC;
  }
}
